package fr.frux.shared.domain.photos

import kotlinx.coroutines.flow.StateFlow

/**
 * Une photo à envoyer. [id] est choisi par l'appelant, unique par arbre, et identifie la photo
 * dans [TreePhotoUploads.uploads].
 */
class PhotoUploadRequest(
    val id: String,
    val bytes: ByteArray,
    val contentType: String,
)

sealed interface PhotoUploadState {
    data object Waiting : PhotoUploadState

    /** [fraction] vaut `null` tant que la progression n'est pas connue. */
    data class Sending(
        val fraction: Float?,
    ) : PhotoUploadState

    data object Sent : PhotoUploadState

    data object Failed : PhotoUploadState
}

data class PhotoUpload(
    val id: String,
    val state: PhotoUploadState,
)

/**
 * File applicative d'envoi des photos d'arbres. Elle survit à la fermeture de l'assistant de
 * création : l'arbre existe déjà, ses photos partent ensuite une par une. Une photo en échec garde
 * ses octets jusqu'à [retry], [remove] ou [clear] : la fiche de l'arbre (lot 4) en est l'appelant.
 */
interface TreePhotoUploads {
    /** Envois par arbre, dans l'ordre d'enfilement. */
    val uploads: StateFlow<Map<Long, List<PhotoUpload>>>

    fun enqueue(
        treeId: Long,
        photos: List<PhotoUploadRequest>,
    )

    /** Repasse une photo `Failed` en `Waiting` et la renvoie. Sans effet sur une autre photo. */
    fun retry(
        treeId: Long,
        id: String,
    )

    /** Oublie une photo `Failed` et ses octets. Sans effet sur une autre photo. */
    fun remove(
        treeId: Long,
        id: String,
    )

    /** Purge les photos `Sent` de l'arbre, une fois la fiche rechargée avec leur résultat. */
    fun forgetSent(treeId: Long)

    /** Annule les envois en cours et oublie tout, à la déconnexion. */
    fun clear()
}
